/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import InternetDetailsCard from "./InternetDetailsCard";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { mapConnectionDetailToSections } from "kontrolEdge/constants/constants";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { ServiceDetailsPageSkeleton } from "kontrolEdge/components/Shared/ServiceDetailsPageSkeleton";
import { fetchConnectionBgpAPI, fetchConnectionErrorsAPI, fetchConnectionLatencyAPI, fetchConnectionSubscriptionAPI, fetchInternetConnectionAPI, fetchInternetConnectionSubnetsAPI } from "../requests/request";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { RootState } from "@redux/store/store";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { setSelectedLatencyDest } from "@redux/slices/kontrolEdge/connectionSlice";
import BandwidthChartUniversal from "kontrolEdge/components/Shared/Bandwidth/BandwidthChartUniversal";
import { apiTagsToSummaries } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";

// 1) The overall response shape
export interface InternetConnectionResponse {
  success: boolean;
  message: string;
  data: RawConnectionDetail;
}

export interface Subnet {
  id: number;
  ip: string;
  mask: string;
  status: string;
}
export interface BgpNeighbor {
  peeringType: string;
  neighborIpAddress: string;
  prefixesAccepted: number;
  remoteRouterId: string;
  localASN: number;
  remoteASN: number;
  advertisedPaths: number;
  state: { isConnected: boolean };
  sourceIp?: string;
}

export interface BgpNeighborsPayload {
  serviceType?: string;
  bgpNeighbors: BgpNeighbor[];
}

export interface BgpDetail {
  // mirror the shape returned by fetchConnectionBgpAPI:
  peeringType: string;
  neighborIpAddress: string;
  state: { isConnected: boolean };
}

export type LatencyDetail = { 
  value: number;
  unit?: string;
  destination?: string;
  timestamp?: string 
};

export interface SubscriptionDetail {
  subscriptionId: string;
  // …other fields…
}

// 2) The actual “data” payload
export interface RawConnectionDetail {
  id: number;
  mbps: number;
  vlan: number;
  vlanSecondary?: number | null;
  automaticVlan: boolean;
  redundancy: boolean;
  routing: string;
  terms: number;
  autoRenew: boolean;
  p2pAddressing: {
    mask: string;
    peerIp: string;
    gateway: string;
    network: string;
    vlanSecondary?: number | null;
    maskSecondary?: string;
    peerIpSecondary?: string;
    gatewaySecondary?: string;
    networkSecondary?: string;
  };
  bgp?: BgpDetail; // legacy single-peer shape (keep for fallback)
  bgpServiceType?: string;         // NEW
  bgpNeighbors?: BgpNeighbor[];
  subscriptionId: string;
  port: {
    id: number;
    name: string;
    datacenter: {
      datacenterId: string;
      name: string;
      displayName: string;
      direction: string;
      gps: string;
      capacityKW: string;
      phase: string;
      cage: string;
      rackId: string;
      rackCapacity: string;
      patchPanelODF: string;
      rackSecurityPin: string;
      status: string;
      visible: boolean;
      providerCode: string;
      city: string;
      providerId: number;
      createdAt: string;
      updatedAt: string;
    };
  };
  lag: null;
  fwaas?: {
    id: number;
    name: string;
  } | null;
  sdwanHub?: {
    id: number;
    name: string;
    subscriptionId: string | null;
  } | null;
  performanceMetrics?: {
    errors: number;
    latency?: LatencyDetail
  };
  subscription?: SubscriptionDetail;
  subnets: Array<{
    id: number;
    ip: string;
    mask: string;
    status: string;
  }>;
}

type LocationState = { connectionDetail?: RawConnectionDetail, tenantOverride?: number; };

const InternetDetails: React.FC = () => {
	//Grab the raw Location object
	const location = useLocation();
  const reduxTenantId = useAppSelector((state) => state.selection.tenantId);
	//Assert its state shape
	const { connectionDetail: preloaded, tenantOverride } =
		(location.state as LocationState) || {};
  const [searchParams] = useSearchParams();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const connectionId = searchParams.get("connectionId")!;

	//Seed from navigation‑state if available
	const [detail, setDetail] = useState<RawConnectionDetail | undefined>(preloaded);
	const [isFetching, setIsFetching] = useState(false);
	const api = useAxiosAuth0();
  const userProfile = useAppSelector((state: RootState) => state.userProfile);
  const { canAccessAlerts } = userProfile;
  const effectiveTenantId = tenantOverride ?? reduxTenantId;
  const { tenantId } = useAppSelector((state) => state.selection);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  // Track the initial tenantId when component mounts to detect changes
  const initialTenantIdRef = useRef<number | null>(null);
  
// Initialize the ref on mount
useEffect(() => {
  if (initialTenantIdRef.current === null) {
    initialTenantIdRef.current = tenantId;
  }
}, []);

// Redirect to main view if tenantId changes while on detail page
useEffect(() => {
  // Skip if ref hasn't been initialized yet
  if (initialTenantIdRef.current === null) return;
  
  // Only redirect if tenantId actually changed (not on initial mount)
  if (tenantId !== initialTenantIdRef.current) {
    navigate("/kontrol/ipx");
  }
}, [tenantId, navigate]);

  const { performanceData } = useAppSelector(state => state.connection);
  //Grab the first reading in the array:
  const rawReadings = performanceData?.latencyReadings ?? [];

  const [latencyLoading, setLatencyLoading] = useState(false);
  const [fetchedLatency, setFetchedLatency] = useState<LatencyDetail | undefined>(undefined);

  // we'll skip the effect the first time to avoid an initial spinner/fetch
  const skipFirstLatencyFetchRef = useRef(true);

  function normalizeLatency(
    raw: any,
    destination: string
  ): LatencyDetail | undefined {
    if (!raw) return undefined;
    const v =
      typeof raw.value === "number" ? raw.value :
      typeof raw.value === "string" ? Number(raw.value) :
      typeof raw.latencyMs === "number" ? raw.latencyMs :
      typeof raw.avg === "number" ? raw.avg :
      typeof raw.mean === "number" ? raw.mean :
      undefined;
  
    if (v == null || Number.isNaN(v)) return undefined;
  
    const unit =
      typeof raw.unit === "string" && raw.unit.trim()
        ? raw.unit
        : "ms";
  
    return {
      value: v,
      unit,
      destination: destination ?? raw.destination,
      timestamp: raw.timestamp ?? raw.time ?? undefined,
    };
  }


// Destination list, e.g. ["8.8.8.8", "1.1.1.1"]
const latencyOptions = useMemo(() => {
  const list = rawReadings
    .map(r => r?.destination)
    .filter((d): d is string => Boolean(d));
  const seen = new Set<string>();
  const orderedUnique: string[] = [];
  for (const d of list) {
    if (!seen.has(d)) {
      seen.add(d);
      orderedUnique.push(d);
    }
  }
  return orderedUnique;
}, [rawReadings]);


// Selected destination
const [latencyDest, setLatencyDest] = useState<string | undefined>(() =>
  latencyOptions[0]
);

useEffect(() => {
  if (!connectionId || !latencyDest) return;

  if (skipFirstLatencyFetchRef.current) {
    skipFirstLatencyFetchRef.current = false;
    return;
  }
  let cancelled = false;
  setLatencyLoading(true);
  (async () => {
    try {
      const resp = await fetchConnectionLatencyAPI(
        api,
        connectionId,
        effectiveTenantId,
        latencyDest
      );

      if (cancelled) return;

      const normalized = normalizeLatency(resp?.data?.data, latencyDest);
      setFetchedLatency(normalized);
    } catch (err) {
      console.error("Latency fetch failed:", err);
      setFetchedLatency(undefined);
    } finally {
      if (!cancelled) setLatencyLoading(false);
    }
  })();

  return () => { cancelled = true; };
}, [api, connectionId, effectiveTenantId, latencyDest]);

// Reading to display (falls back to first if not found)
const selectedLatency: LatencyDetail | undefined = useMemo(() => {
  if (!rawReadings.length) return undefined;
  if (latencyDest) {
    return (
      rawReadings.find((r) => r.destination === latencyDest) ?? rawReadings[0]
    );
  }
  return rawReadings[0];
}, [rawReadings, latencyDest]);

// Build the dropdown control (only if multiple choices)
const latencyDropdown =
  latencyOptions.length > 1 ? (
    <GenericDropdown
      items={latencyOptions}
      value={latencyDest ?? ""}
      onSelect={(dest) => {
        if (dest === latencyDest) return;
        setLatencyLoading(true);
        setLatencyDest(dest);
      }}
      size="sm"
      widthClass="w-36"
      defaultLabel="Select IP"
    />
  ) : null;


  ////////
  const effectiveLatency: LatencyDetail | undefined = fetchedLatency ?? selectedLatency;

  const sections = useMemo(
    () =>
      mapConnectionDetailToSections(
        { data: detail },
        effectiveLatency,
        { latencyControl: latencyDropdown }
      ),
    [detail, effectiveLatency, latencyDropdown]
  );

  // Rich tags inline from the detail GET (§5.5). When KD-563-2 ships in
  // the active environment the parent already has the rich array, so the
  // resource-tags hook can skip its first §5.1 round-trip.
  const initialAssignedTags = useMemo(
    () => apiTagsToSummaries((detail as { tags?: unknown } | undefined)?.tags),
    [detail]
  );

  // Tenant the resource actually lives in (NOT the SideNav lens). Forwarded
  // to the Tags API so writes hit the right scope — see PortDetails.tsx for
  // the full rationale. Without this, partner-view writes 404 because the
  // backend's resource-side adapter doesn't honor the tenant hierarchy.
  //
  // Falls back to `effectiveTenantId` (the same value the detail GET sent
  // as `?tenantId=` and that the backend validated against) when the
  // response doesn't expose `tenantId` on the body. The Internet detail
  // transform omits this field today (unlike Port), so without the
  // fallback the Tags picker would call without `?tenantId=` and the
  // backend would resolve to the JWT tenant — leaking the partner-level
  // catalog and 404'ing on attach.
  const resourceTenantId = useMemo(() => {
    const t = (detail as { tenantId?: unknown } | undefined)?.tenantId;
    if (typeof t === "number" && Number.isFinite(t)) return t;
    return effectiveTenantId ?? undefined;
  }, [detail, effectiveTenantId]);

const patchDetailMeta = useCallback((p: { name?: string; tag?: string }) => {
  setDetail((d) => (d ? { ...d, ...(p.name ? { name: p.name } : {}), ...(p.tag ? { tag: p.tag } : {}) } : d));
}, []);

    useEffect(() => {
      //Re-run whenever connectionId or refresh changes
      if (!connectionId) return;
      let cancelled = false;
      setIsFetching(true);
    
      (async () => {
        try {
          //Core detail – show it immediately
          const coreResp = await fetchInternetConnectionAPI(api, connectionId, effectiveTenantId);
          const fresh: RawConnectionDetail = coreResp.data.data;
          if (cancelled) return;
          setDetail(fresh);
    
          //Extras in parallel
          const [
            errorsSettled,
            subnetsSettled,
            bgpSettled,
            subscriptionSettled
          ] = await Promise.allSettled([
            fetchConnectionErrorsAPI(api, connectionId, tenantId),
            fetchInternetConnectionSubnetsAPI(api, connectionId,tenantId),
            fetchConnectionBgpAPI(api, connectionId, tenantId),
            fetchConnectionSubscriptionAPI(api, connectionId, tenantId),
          ]);
    
          //Merge successful results
          if (errorsSettled.status === 'fulfilled') {
            fresh.performanceMetrics = {
              ...fresh.performanceMetrics,
              errors: errorsSettled.value.data.data.crcErrors
            };
          } else {
            console.warn('CRC errors failed:', errorsSettled.reason);
          }
    
          if (subnetsSettled.status === 'fulfilled') {
            fresh.subnets = subnetsSettled.value.data.data;
          } else {
            console.warn('Subnets failed:', subnetsSettled.reason);
          }
    
          // if (bgpSettled.status === 'fulfilled') {
          //   fresh.bgp = bgpSettled.value.data.data;
          // } else {
          //   console.warn('BGP failed:', bgpSettled.reason);
          // }

          if (bgpSettled.status === 'fulfilled') {
            const payload: BgpNeighborsPayload | undefined = bgpSettled.value?.data?.data;
            const neighbors = Array.isArray(payload?.bgpNeighbors) ? payload!.bgpNeighbors : [];
            (fresh as any).bgpServiceType = payload?.serviceType;
            (fresh as any).bgpNeighbors = neighbors;
          
            // Back-compat: keep first neighbor in legacy field if someone still reads data.bgp
            fresh.bgp = neighbors[0] ?? undefined;
          } else {
            console.warn('BGP failed:', bgpSettled.reason);
          }
    
          if (subscriptionSettled.status === 'fulfilled') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (fresh as any).subscription = subscriptionSettled.value.data.data;
          } else {
            console.warn('Subscription fetch failed:', subscriptionSettled.reason);
          }
    
          if (cancelled) return;
          //Push the merged object back into state
          setDetail({ ...fresh });
    
        } catch (err) {
          console.error('Error loading InternetDetails:', err);
        } finally {
          if (!cancelled) setIsFetching(false);
        }
      })();
    
      return () => { cancelled = true; };
    }, [api, connectionId]);
		
    // Keep selection valid + push it to Redux when it changes
     // Keep local selection valid; do NOT mirror to Redux from this view
     useEffect(() => {
       if (!latencyOptions.length) {
         if (latencyDest !== undefined) setLatencyDest(undefined);
         return;
       }
       if (!latencyDest || !latencyOptions.includes(latencyDest)) {
         setLatencyDest(latencyOptions[0]);
       }
     }, [latencyOptions, latencyDest]);

    if (isFetching) {
      return <ServiceDetailsPageSkeleton />;
    }
    if (!detail) {
      return (
        <p className="p-4 text-sm text-semantic-text-secondary">
          Connection not found or could not be loaded.
        </p>
      );
    }
	return (
		<>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 auto-rows-auto">
			{sections.map((section, idx) => (
				<InternetDetailsCard key={idx} data={section} onMetaPatched={patchDetailMeta} latencyLoading={latencyLoading} connectionDetail={detail} initialAssignedTags={initialAssignedTags} resourceTenantId={resourceTenantId}/>
			))}
			</div>
			{/* Bandwidth Utilization chart */}
      <BandwidthChartUniversal 
				service="internet"
				id={connectionId}
				tenantId={effectiveTenantId}
				portType="physical"
				destination={latencyDest || undefined}
			/>
      {/* {canAccessAlerts && connectionId && (
        <NotificationsView serviceType="internet" serviceId={connectionId}/>
      )} */}
		</>
	);
};

export default InternetDetails;
